import { uploadLandingBuild } from '@/api/landings';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import type { IBuild } from '@/entities/build.entity';
import type { ILandingUploadedBuild } from '@/entities/landing.entity';
import { CheckIcon, UploadIcon } from 'lucide-react';
import { useRef, useState, type ChangeEvent } from 'react';
import { toast } from 'sonner';

interface LandingApkPickerProps {
	panelBuilds: IBuild[];
	uploadedBuilds: ILandingUploadedBuild[];
	buildId: string;
	onBuildIdChange: (buildId: string) => void;
	onUploaded: (build: ILandingUploadedBuild) => void;
	disabled?: boolean;
}

function formatPanelBuildLabel(build: IBuild): string {
	const name = build.appName ?? build.id;
	const date = new Date(build.createdAt).toLocaleDateString('ru-RU');
	return `${name} · ${date}`;
}

function formatUploadedBuildLabel(build: ILandingUploadedBuild): string {
	const name = build.appName?.trim() || build.apkFileName || build.buildId;
	const date = new Date(build.createdAt).toLocaleDateString('ru-RU');
	return `${name} · ${date}`;
}

function uploadedDisplayName(build: ILandingUploadedBuild): string {
	return build.appName?.trim() || build.apkFileName || build.buildId;
}

export default function LandingApkPicker({
	panelBuilds,
	uploadedBuilds,
	buildId,
	onBuildIdChange,
	onUploaded,
	disabled,
}: LandingApkPickerProps) {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isUploading, setIsUploading] = useState(false);

	const hasAny = panelBuilds.length > 0 || uploadedBuilds.length > 0;
	const knownIds = new Set([
		...panelBuilds.map(b => b.id),
		...uploadedBuilds.map(b => b.buildId),
	]);
	const showCurrentFallback = Boolean(buildId) && !knownIds.has(buildId);
	const selectedUploaded = uploadedBuilds.find(b => b.buildId === buildId);

	function handleValueChange(next: string) {
		if (!next) {
			return;
		}
		onBuildIdChange(next);
	}

	async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
		const file = e.target.files?.[0];
		e.target.value = '';
		if (!file) {
			return;
		}

		setIsUploading(true);
		try {
			const uploaded = await uploadLandingBuild(file);
			onUploaded(uploaded);
			toast.success('APK загружен');
		} finally {
			setIsUploading(false);
		}
	}

	return (
		<div className='flex flex-col gap-1.5'>
			<Label>Сборка APK</Label>
			{hasAny || showCurrentFallback ? (
				<Select
					value={buildId || undefined}
					onValueChange={handleValueChange}
					disabled={disabled || isUploading}
				>
					<SelectTrigger className='w-full'>
						<SelectValue placeholder='Выберите сборку или загрузите APK' />
					</SelectTrigger>
					<SelectContent>
						{showCurrentFallback ? (
							<SelectGroup>
								<SelectLabel>Текущая</SelectLabel>
								<SelectItem value={buildId}>{buildId}</SelectItem>
							</SelectGroup>
						) : null}
						{panelBuilds.length > 0 ? (
							<SelectGroup>
								<SelectLabel>Сборки панели</SelectLabel>
								{panelBuilds.map(b => (
									<SelectItem key={b.id} value={b.id}>
										{formatPanelBuildLabel(b)}
									</SelectItem>
								))}
							</SelectGroup>
						) : null}
						{uploadedBuilds.length > 0 ? (
							<SelectGroup>
								<SelectLabel>Загруженные</SelectLabel>
								{uploadedBuilds.map(b => (
									<SelectItem key={b.buildId} value={b.buildId}>
										{formatUploadedBuildLabel(b)}
									</SelectItem>
								))}
							</SelectGroup>
						) : null}
					</SelectContent>
				</Select>
			) : (
				<p className='text-sm text-muted-foreground'>Нет готовых сборок. Загрузите свой APK или соберите в панели.</p>
			)}
			{selectedUploaded ? (
				<div className='flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm'>
					<CheckIcon className='mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400' />
					<div className='min-w-0'>
						<p className='truncate font-medium'>{uploadedDisplayName(selectedUploaded)}</p>
						<p className='text-xs text-muted-foreground'>APK загружен и выбран</p>
					</div>
				</div>
			) : null}
			<input
				ref={fileInputRef}
				type='file'
				accept='.apk,application/vnd.android.package-archive'
				className='hidden'
				disabled={disabled || isUploading}
				onChange={handleFileChange}
			/>
			<Button
				type='button'
				variant='outline'
				size='sm'
				className='self-start'
				disabled={disabled || isUploading}
				onClick={() => fileInputRef.current?.click()}
			>
				<UploadIcon />
				{isUploading ? 'Загрузка…' : 'Загрузить APK'}
			</Button>
		</div>
	);
}
